import React, { useState, useEffect, useRef } from 'react';
import AddIcon from '@mui/icons-material/Add';

const CustomerAutocomplete = ({ 
  value, 
  onChange, 
  options, 
  label, 
  onAddNew,
  placeholder = "Müşteri ara...",
  error = false,
  errorMessage = ""
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const wrapperRef = useRef(null);
  
  // Initialize the input value based on the selected value
  useEffect(() => {
    if (value) {
      const selectedOption = options.find(option => option.name === value);
      if (selectedOption) {
        setInputValue(selectedOption.name);
      } else {
        setInputValue('');
      }
    } else {
      setInputValue('');
    }
  }, [value, options]);

  // Filter options based on input value
  useEffect(() => {
    if (!inputValue) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option => 
        option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
        (option.contact_person && option.contact_person.toLowerCase().includes(inputValue.toLowerCase())) ||
        (option.email && option.email.toLowerCase().includes(inputValue.toLowerCase())) ||
        (option.phone && option.phone.toLowerCase().includes(inputValue.toLowerCase())) ||
        (option.city && option.city.toLowerCase().includes(inputValue.toLowerCase())) ||
        (option.country && option.country.toLowerCase().includes(inputValue.toLowerCase()))
      );
      setFilteredOptions(filtered);
    }
  }, [inputValue, options]);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        
        // Reset input to selected value if clicked outside
        if (value) {
          const selectedOption = options.find(option => option.name === value);
          if (selectedOption) {
            setInputValue(selectedOption.name);
          }
        }
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [value, options]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setIsOpen(true);
    
    // If input is cleared, also clear the selected value
    if (!e.target.value) {
      onChange('');
    }
  };

  const handleOptionClick = (option) => {
    onChange(option.name);
    setInputValue(option.name);
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {label.includes('*') ? '' : <span className="text-red-500">*</span>}
      </label>
      <div className="flex space-x-2">
        <div className="relative flex-1">
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            autoComplete="new-password"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {isOpen && filteredOptions.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
              {filteredOptions.map((option) => (
                <div
                  key={option.id}
                  onClick={() => handleOptionClick(option)}
                  className="px-4 py-2 hover:bg-blue-50 cursor-pointer"
                >
                  <div className="font-medium">{option.name}</div>
                  <div className="text-sm text-gray-500">
                    {option.contact_person && `${option.contact_person} - `}
                    {option.phone && `${option.phone} - `}
                    {option.city && option.country && `${option.city}, ${option.country}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onAddNew}
          className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
        >
          <AddIcon className="w-4 h-4 mr-1" />
          Müşteri Ekle
        </button>
      </div>
      {error && errorMessage && (
        <p className="error-message text-red-500 text-xs mt-1">
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default CustomerAutocomplete;
